import { createSlice } from '@reduxjs/toolkit';
import { RootState } from '../..';
import {
    FlightNumberEntity,
    FlightNumbersResponse,
    UpsertFlightNumberResult,
    fetchFlightNumbers,
    upsertFlightNumber,
    deleteFlightNumber,
} from './flightNumbersAPI';
import { IPagination, LoadStatus } from '../../../model';
import { HaulType } from '../../../model';
import { createAppThunk } from '../../thunks';

export interface FlightNumbersPanelState {
    items: FlightNumberEntity[];
    nextPageToken: string | null;
    search: string | null;
    loadStatus: LoadStatus;
    pagination: IPagination;
    lastUpsertStatus?: UpsertFlightNumberResult['status'] | 'error';
    lastUpsertMessage?: string;
}

const initialState: FlightNumbersPanelState = {
    items: [],
    nextPageToken: null,
    search: null,
    loadStatus: 'loading',
    pagination: {
        currentPage: 1,
        pageSize: 10,
        totalCount: 0,
    },
};

export const loadFlightNumbers = createAppThunk<FlightNumbersResponse>(
    'flightNumbersPanel/loadFlightNumbers',
    async (_, { getState }) => {
        const {
            flightNumbersPanel: { nextPageToken, search },
        } = getState();

        const queryParams = new Map<string, string>();

        if (search) queryParams.set('search', search);

        if (nextPageToken)
            queryParams.set('tableContinuationToken', nextPageToken as any);

        const queryParts: string[] = [];

        queryParams.forEach((value, key) => {
            queryParts.push(
                queryParts.length ? `&${key}=${value}` : `?${key}=${value}`
            );
        });

        return await fetchFlightNumbers(queryParts.join(''));
    }
);

export const deleteFlightNumbers = createAppThunk<void, string>(
    'flightNumbersPanel/deleteFlightNumbers',
    async (flightNumber) => await deleteFlightNumber(flightNumber)
);

export const searchFlightNumbers = createAppThunk<
    FlightNumbersResponse,
    string
>(
    'flightNumbersPanel/searchFlightNumbers',
    async (search) => await fetchFlightNumbers(`?search=${search}`)
);

export const upsertFlightNumbers = createAppThunk<
    UpsertFlightNumberResult,
    { flightNumber: string; isPermanent: boolean; haulType: HaulType }
>(
    'flightNumbersPanel/upsertFlightNumbers',
    async (dto) => await upsertFlightNumber(dto)
);

const getUnique = (items: FlightNumberEntity[]) => {
    const map = new Map(items.map((i) => [i.flightNumber, i]));
    return Array.from(map.values()).sort((a, b) =>
        a.flightNumber.localeCompare(b.flightNumber)
    );
};

export const flightNumbersSlice = createSlice({
    name: 'flightNumbersPanel',
    initialState,
    reducers: {
        resetUpsertFeedback(state) {
            state.lastUpsertStatus = undefined;
            state.lastUpsertMessage = undefined;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(loadFlightNumbers.pending, (state) => {
                state.loadStatus = 'loading';
            })
            .addCase(loadFlightNumbers.fulfilled, (state, a) => {
                state.loadStatus = 'idle';
                state.items = getUnique([
                    ...state.items,
                    ...(a.payload.items ?? []),
                ]);
                state.nextPageToken = a.payload.nextPageToken
                    ? JSON.stringify(a.payload.nextPageToken)
                    : null;
                state.search = a.payload.search;
            })
            .addCase(loadFlightNumbers.rejected, (state) => {
                state.loadStatus = 'failed';
            })
            .addCase(searchFlightNumbers.pending, (state) => {
                state.loadStatus = 'loading';
            })
            .addCase(searchFlightNumbers.fulfilled, (state, a) => {
                state.loadStatus = 'idle';
                state.items = a.payload.items ?? [];
                state.nextPageToken = a.payload.nextPageToken
                    ? JSON.stringify(a.payload.nextPageToken)
                    : null;
                state.search = a.payload.search;
            })
            .addCase(searchFlightNumbers.rejected, (state) => {
                state.loadStatus = 'failed';
            })
            .addCase(upsertFlightNumbers.pending, (state) => {
                state.loadStatus = 'loading';
                // clear previous feedback
                state.lastUpsertStatus = undefined;
                state.lastUpsertMessage = undefined;
            })
            .addCase(upsertFlightNumbers.fulfilled, (state, a) => {
                state.loadStatus = 'idle';
                const result = a.payload;
                state.lastUpsertStatus = result.status;
                state.lastUpsertMessage = result.message;

                const passesSearch = (fn: string) =>
                    !state.search ||
                    fn.toUpperCase().includes(state.search.toUpperCase());

                if (result.status === 'duplicate') {
                    return;
                }

                if (
                    result.status === 'success' &&
                    result.entity &&
                    passesSearch(result.entity.flightNumber)
                ) {
                    // replace existing then unique
                    state.items = getUnique([
                        ...state.items.filter(
                            (i) =>
                                i.flightNumber !== result.entity!.flightNumber
                        ),
                        result.entity!,
                    ]);
                }
            })
            .addCase(upsertFlightNumbers.rejected, (state) => {
                state.loadStatus = 'failed';
                state.lastUpsertStatus = 'error';
                state.lastUpsertMessage = 'Failed to update flight number.';
            })
            .addCase(deleteFlightNumbers.fulfilled, (state, action) => {
                state.items = state.items.filter((item) => item.flightNumber !== action.meta.arg);
            })
            .addCase(deleteFlightNumbers.rejected, (state) => {
                state.loadStatus = 'failed';
            });
    },
});

export const selectFlightNumbersPanel = (state: RootState) =>
    state.flightNumbersPanel;

export const selectFlightNumbersUpsertFeedback = (state: RootState) => ({
    status: state.flightNumbersPanel.lastUpsertStatus,
    message: state.flightNumbersPanel.lastUpsertMessage,
});
export const { resetUpsertFeedback } = flightNumbersSlice.actions;
export default flightNumbersSlice.reducer;
